import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Calendar, MapPin, Users, Globe, ExternalLink,
  Repeat, Tag, Building2, ChevronRight, Star,
  TrendingUp, Award, MessageSquare
} from "lucide-react";
const API = process.env.REACT_APP_BACKEND_URL + "/api";

const CATEGORY_COLORS = {
  "Teknoloji":           "bg-blue-100 text-blue-700 border-blue-200",
  "Otomotiv":            "bg-gray-100 text-gray-700 border-gray-200",
  "Gıda & İçecek":       "bg-orange-100 text-orange-700 border-orange-200",
  "Moda & Tekstil":      "bg-pink-100 text-pink-700 border-pink-200",
  "Mobilya & Tasarım":   "bg-amber-100 text-amber-700 border-amber-200",
  "Sanayi & Makine":     "bg-slate-100 text-slate-700 border-slate-200",
  "Sağlık & Medikal":    "bg-green-100 text-green-700 border-green-200",
  "İnşaat & Gayrimenkul":"bg-yellow-100 text-yellow-700 border-yellow-200",
  "Turizm & Seyahat":    "bg-cyan-100 text-cyan-700 border-cyan-200",
  "Enerji":              "bg-lime-100 text-lime-700 border-lime-200",
  "Tarım":               "bg-emerald-100 text-emerald-700 border-emerald-200",
  "Güvenlik & Savunma":  "bg-red-100 text-red-700 border-red-200",
  "Perakende":           "bg-violet-100 text-violet-700 border-violet-200",
  "Diğer":               "bg-gray-50 text-gray-500 border-gray-100",
};

function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" });
  } catch { return dateStr; }
}

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
      <div className={`w-10 h-10 rounded-xl bg-${color}-50 flex items-center justify-center mb-3`}>
        <Icon className={`w-5 h-5 text-${color}-600`} />
      </div>
      <p className="text-xs text-gray-500 mb-1">{label}</p>
      <p className="text-lg font-bold text-gray-900">{value || "—"}</p>
    </motion.div>
  );
}

export default function FairDetail() {
  const { fairId } = useParams();
  const navigate = useNavigate();
  const [fair, setFair] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    Promise.all([
      fetch(`${API}/public/fairs/${fairId}`).then(r => {
        if (!r.ok) throw r.status;
        return r.json();
      }),
      fetch(`${API}/public/fairs/${fairId}/contractors`)
        .then(r => r.ok ? r.json() : { contractors: [] }),
    ])
      .then(([fairData, contractorData]) => {
        setFair(fairData);
        setContractors(contractorData.contractors || []);
      })
      .catch(e => setError(e === 404 ? "Fuar bulunamadı." : "Bir hata oluştu."))
      .finally(() => setLoading(false));
  }, [fairId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !fair) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <p className="text-5xl mb-6">🔍</p>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Fuar Bulunamadı</h1>
          <p className="text-gray-500 mb-8">{error || "Bu fuar mevcut değil."}</p>
          <button onClick={() => navigate(-1)}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold">
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  const citySlug = fair.city?.toLowerCase().replace(/\s+/g, "-");
  const catColor = CATEGORY_COLORS[fair.category] || CATEGORY_COLORS["Diğer"];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="fair-detail-page">

      {/* Hero */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-8">
            <Link to="/" className="hover:text-white transition-colors">Ana Sayfa</Link>
            <ChevronRight className="w-4 h-4" />
            {citySlug && (
              <>
                <Link to={`/city/${citySlug}`} className="hover:text-white transition-colors">{fair.city}</Link>
                <ChevronRight className="w-4 h-4" />
              </>
            )}
            <span className="text-slate-300 truncate max-w-xs">{fair.name}</span>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-start gap-10">
            <div className="flex-1">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border mb-5 ${catColor}`}>
                <Tag className="w-3 h-3" />{fair.category || "Diğer"}
              </span>
              <h1 className="text-3xl lg:text-5xl font-black text-white mb-4 leading-tight">
                {fair.name}
              </h1>
              {fair.description && (
                <p className="text-lg text-slate-300 leading-relaxed mb-6 max-w-2xl">
                  {fair.description}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center gap-2 text-slate-300">
                  <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0" />
                  <span>{fair.venue ? `${fair.venue}, ` : ""}{fair.city}{fair.country ? `, ${fair.country}` : ""}</span>
                </div>
                {fair.start_date ? (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Calendar className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span>
                      {formatDate(fair.start_date)}
                      {fair.end_date && fair.end_date !== fair.start_date
                        ? ` – ${formatDate(fair.end_date)}` : ""}
                    </span>
                  </div>
                ) : fair.date_raw ? (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Calendar className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span>{fair.date_raw}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Calendar className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    <span className="text-sm italic">Tarih açıklanacak</span>
                  </div>
                )}
                {fair.frequency && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <Repeat className="w-4 h-4 text-blue-400 flex-shrink-0" />
                    <span>{fair.frequency}</span>
                  </div>
                )}
              </div>
            </div>

            {/* CTA Card */}
            <div className="lg:w-80 flex-shrink-0">
              <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-2xl p-6 space-y-4">
                <h3 className="font-bold text-white text-lg">Bu Fuarda Stand Mı Arıyorsunuz?</h3>
                <p className="text-slate-300 text-sm">Doğrulanmış contractor'lardan ücretsiz teklif alın.</p>
                {contractors.length > 0 && (
                  <div className="flex items-center gap-2 text-sm text-green-400">
                    <Award className="w-4 h-4" />
                    <span>{contractors.length} contractor bu fuarda çalışıyor</span>
                  </div>
                )}
                <Link
                  to={`/get-quote?fair=${encodeURIComponent(fair.name)}&city=${encodeURIComponent(fair.city || "")}`}
                  className="w-full block text-center bg-blue-600 hover:bg-blue-500 text-white font-semibold py-3 rounded-xl transition-colors"
                  data-testid="fair-get-quote-btn"
                >
                  Ücretsiz Teklif Al
                </Link>
                {fair.website && (
                  <a href={fair.website} target="_blank" rel="noreferrer"
                    className="w-full flex items-center justify-center gap-2 text-slate-300 hover:text-white text-sm transition-colors py-2">
                    <Globe className="w-4 h-4" />Resmi Web Sitesi<ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-12 space-y-12">

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Users}     label="Ziyaretçi Sayısı"    value={fair.visitors !== "—" ? fair.visitors : undefined}     color="blue" />
          <StatCard icon={Building2} label="Katılımcı Firma"      value={fair.exhibitors !== "—" ? fair.exhibitors : undefined} color="purple" />
          <StatCard icon={Repeat}    label="Döngü"                value={fair.frequency}                                         color="emerald" />
          <StatCard icon={Award}     label="Çalışan Contractor"   value={contractors.length > 0 ? `${contractors.length}+` : undefined} color="orange" />
        </div>

        {/* Contractors */}
        {contractors.length > 0 ? (
          <section data-testid="fair-contractors-section">
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Bu Fuarda Çalışan Contractor'lar</h2>
              <p className="text-gray-500 text-sm mt-1">{contractors.length} doğrulanmış firma bu fuarda hizmet veriyor</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {contractors.map((c, i) => (
                <motion.div key={c.company_id || i}
                  initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                  className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md transition-all duration-200 cursor-pointer group"
                  onClick={() => navigate(`/contractor/${c.company_id}`)}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {c.logo
                        ? <img src={c.logo} alt={c.company_name} className="w-full h-full object-cover" />
                        : <span className="text-lg font-bold text-blue-600">{(c.company_name || "?")[0].toUpperCase()}</span>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors truncate">{c.company_name}</h3>
                      {c.tagline && <p className="text-xs text-gray-500 mt-0.5 truncate">{c.tagline}</p>}
                      <div className="flex items-center gap-3 mt-2">
                        {c.rating > 0 && (
                          <div className="flex items-center gap-1 text-xs text-amber-600">
                            <Star className="w-3 h-3 fill-amber-400" />{c.rating.toFixed(1)}
                          </div>
                        )}
                        {(c.city || c.country) && (
                          <div className="flex items-center gap-1 text-xs text-gray-400">
                            <MapPin className="w-3 h-3" />{c.city || c.country}
                          </div>
                        )}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-blue-500 flex-shrink-0 mt-1 transition-colors" />
                  </div>
                  {c.specializations?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-3">
                      {c.specializations.slice(0, 3).map((s, j) => (
                        <span key={j} className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full border border-gray-100">{s}</span>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </section>
        ) : (
          <section className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border border-blue-100 p-10 text-center">
            <div className="text-5xl mb-5">🏗️</div>
            <h3 className="text-xl font-bold text-slate-800 mb-2">Henüz Kayıtlı Contractor Yok</h3>
            <p className="text-slate-500 max-w-lg mx-auto mb-6">
              {fair.name} fuarında çalışan contractor'lar henüz profillerini güncellememiş.
              Siz de bu fuarı profilinize ekleyin ve öne çıkın!
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to={`/get-quote?fair=${encodeURIComponent(fair.name)}`}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-semibold text-sm">
                Teklif İste
              </Link>
              <Link to="/contractor/signup"
                className="px-6 py-3 bg-white text-blue-600 border border-blue-200 rounded-xl hover:bg-blue-50 font-semibold text-sm">
                Contractor Olarak Kaydol
              </Link>
            </div>
          </section>
        )}

        {/* Related fairs */}
        {fair.related_fairs?.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              {fair.city}'daki Diğer Fuarlar
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {fair.related_fairs.map((rf, i) => (
                <motion.div key={rf.fair_id}
                  initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: i * 0.07 }}>
                  <Link to={`/fair/${rf.fair_id}`}
                    className="block bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md hover:border-blue-200 transition-all duration-200 group h-full">
                    <Calendar className="w-6 h-6 text-blue-500 mb-3" />
                    <h3 className="font-bold text-gray-900 text-sm group-hover:text-blue-600 transition-colors leading-tight mb-2">
                      {rf.name}
                    </h3>
                    {rf.start_date && (
                      <p className="text-xs text-blue-600 font-medium mb-1">{rf.start_date.slice(0, 7).replace("-", "/")}</p>
                    )}
                    {rf.venue && <p className="text-xs text-gray-400 truncate">{rf.venue}</p>}
                    {rf.visitors && rf.visitors !== "—" && (
                      <p className="text-xs text-gray-400 mt-1">{rf.visitors} ziyaretçi</p>
                    )}
                  </Link>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Bottom CTA */}
        <section className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-3xl p-10 text-white text-center">
          <h2 className="text-3xl font-black mb-3">{fair.name}'da Stand Kurdurmak İster misiniz?</h2>
          <p className="text-blue-100 mb-8 max-w-xl mx-auto">
            ExpoStand ile doğrulanmış contractor'lardan dakikalar içinde teklif alın.
            {fair.city && ` ${fair.city}'daki en iyi stand firmaları sizin için hazır.`}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to={`/get-quote?fair=${encodeURIComponent(fair.name)}&city=${encodeURIComponent(fair.city || "")}`}
              className="px-8 py-4 bg-white text-blue-700 rounded-xl font-bold hover:bg-blue-50 transition-colors"
              data-testid="fair-bottom-cta"
            >
              Ücretsiz Teklif Al
            </Link>
            {citySlug && (
              <Link to={`/city/${citySlug}`}
                className="px-8 py-4 bg-white bg-opacity-20 text-white border border-white border-opacity-30 rounded-xl font-semibold hover:bg-opacity-30 transition-colors">
                {fair.city} Sayfasına Dön
              </Link>
            )}
          </div>
        </section>

      </div>
    </div>
  );
}
